<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useVuelidate } from '@vuelidate/core';
import { required, url } from '@vuelidate/validators';

import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';

const props = defineProps({
  server: { type: Object, default: null },
});

const emit = defineEmits(['close', 'saved']);

const { t } = useI18n();
const store = useStore();

const dialogRef = ref(null);

const isEdit = computed(() => Boolean(props.server?.id));

const initialState = () => ({
  name: '',
  url: '',
  transport: 'streamable_http',
  authType: 'none',
  authToken: '',
  authHeaderName: '',
  authHeaderValue: '',
  enabled: true,
});

const state = reactive(initialState());

// Server never echoes secrets; surface presence flags so the masked
// inputs render a "saved — leave blank to keep" placeholder. Same
// shape as Captain::Assistant#has_llm_api_key in Phase 2.
const hasSavedToken = computed(() => Boolean(props.server?.has_auth_token));
const hasSavedHeaderValue = computed(() =>
  Boolean(props.server?.has_auth_header_value)
);

const populateFromServer = server => {
  Object.assign(state, initialState());
  if (!server) return;
  state.name = server.name || '';
  state.url = server.url || '';
  state.transport = server.transport || 'streamable_http';
  state.authType = server.auth_type || 'none';
  state.authHeaderName = server.auth_header_name || '';
  state.enabled = server.enabled !== false;
};

watch(
  () => props.server,
  newServer => populateFromServer(newServer),
  { immediate: true }
);

const rules = computed(() => ({
  name: { required },
  url: { required, url },
  // Auth-type-conditional: only validate the relevant secret field when
  // we're creating a new record (in edit mode, leaving blank means
  // "preserve saved value", so emptiness is fine).
  authToken: state.authType === 'bearer' && !isEdit.value ? { required } : {},
  authHeaderName: state.authType === 'header' ? { required } : {},
  authHeaderValue:
    state.authType === 'header' && !isEdit.value ? { required } : {},
}));

const v$ = useVuelidate(rules, state);

const buildPayload = () => {
  const payload = {
    name: state.name,
    url: state.url,
    transport: state.transport,
    auth_type: state.authType,
    enabled: state.enabled,
  };

  // Same secret-handling semantics as Phase 2's llm_api_key field:
  // blank submission preserves the saved secret on edit; on create we
  // require a value (validation enforces this).
  if (state.authType === 'bearer' && state.authToken !== '') {
    payload.auth_token = state.authToken;
  }
  if (state.authType === 'header') {
    payload.auth_header_name = state.authHeaderName;
    if (state.authHeaderValue !== '') {
      payload.auth_header_value = state.authHeaderValue;
    }
  }

  return payload;
};

const handleSubmit = async () => {
  const valid = await v$.value.$validate();
  if (!valid) return;

  try {
    if (isEdit.value) {
      await store.dispatch('captainMcpServers/update', {
        id: props.server.id,
        ...buildPayload(),
      });
      useAlert(t('CAPTAIN.MCP_SERVERS.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('captainMcpServers/create', buildPayload());
      useAlert(t('CAPTAIN.MCP_SERVERS.CREATE_SUCCESS'));
    }
    emit('saved');
    dialogRef.value?.close();
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.MCP_SERVERS.SAVE_ERROR'));
  }
};

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEdit
        ? t('CAPTAIN.MCP_SERVERS.EDIT_TITLE')
        : t('CAPTAIN.MCP_SERVERS.CREATE_TITLE')
    "
    :confirm-button-label="
      isEdit ? t('CAPTAIN.MCP_SERVERS.SAVE') : t('CAPTAIN.MCP_SERVERS.CREATE')
    "
    @confirm="handleSubmit"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4">
      <Input
        v-model="state.name"
        :label="t('CAPTAIN.MCP_SERVERS.FORM.NAME.LABEL')"
        :placeholder="t('CAPTAIN.MCP_SERVERS.FORM.NAME.PLACEHOLDER')"
        :message="
          v$.name.$error ? t('CAPTAIN.MCP_SERVERS.FORM.NAME.ERROR') : ''
        "
        :message-type="v$.name.$error ? 'error' : 'info'"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.MCP_SERVERS.FORM.TRANSPORT.LABEL') }}
        </label>
        <select
          v-model="state.transport"
          class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
        >
          <option value="streamable_http">
            {{
              t('CAPTAIN.MCP_SERVERS.FORM.TRANSPORT.OPTIONS.STREAMABLE_HTTP')
            }}
          </option>
          <option value="sse">
            {{ t('CAPTAIN.MCP_SERVERS.FORM.TRANSPORT.OPTIONS.SSE') }}
          </option>
        </select>
      </div>

      <Input
        v-model="state.url"
        :label="t('CAPTAIN.MCP_SERVERS.FORM.URL.LABEL')"
        :placeholder="t('CAPTAIN.MCP_SERVERS.FORM.URL.PLACEHOLDER')"
        :message="
          v$.url.$error
            ? t('CAPTAIN.MCP_SERVERS.FORM.URL.ERROR')
            : t('CAPTAIN.MCP_SERVERS.FORM.URL.HELP')
        "
        :message-type="v$.url.$error ? 'error' : 'info'"
      />

      <div class="flex flex-col gap-2">
        <label class="text-sm text-n-slate-12">
          {{ t('CAPTAIN.MCP_SERVERS.FORM.AUTH_TYPE.LABEL') }}
        </label>
        <select
          v-model="state.authType"
          class="w-full px-3 py-2 text-sm border rounded-lg bg-n-alpha-black2 border-n-weak text-n-slate-12 focus:outline-none focus:border-n-brand"
        >
          <option value="none">
            {{ t('CAPTAIN.MCP_SERVERS.FORM.AUTH_TYPE.OPTIONS.NONE') }}
          </option>
          <option value="bearer">
            {{ t('CAPTAIN.MCP_SERVERS.FORM.AUTH_TYPE.OPTIONS.BEARER') }}
          </option>
          <option value="header">
            {{ t('CAPTAIN.MCP_SERVERS.FORM.AUTH_TYPE.OPTIONS.HEADER') }}
          </option>
        </select>
      </div>

      <Input
        v-if="state.authType === 'bearer'"
        v-model="state.authToken"
        type="password"
        :label="t('CAPTAIN.MCP_SERVERS.FORM.AUTH_TOKEN.LABEL')"
        :placeholder="
          hasSavedToken
            ? t('CAPTAIN.MCP_SERVERS.FORM.AUTH_TOKEN.PLACEHOLDER_SAVED')
            : t('CAPTAIN.MCP_SERVERS.FORM.AUTH_TOKEN.PLACEHOLDER')
        "
        :message="
          v$.authToken.$error
            ? t('CAPTAIN.MCP_SERVERS.FORM.AUTH_TOKEN.ERROR')
            : ''
        "
        :message-type="v$.authToken.$error ? 'error' : 'info'"
      />

      <template v-if="state.authType === 'header'">
        <Input
          v-model="state.authHeaderName"
          :label="t('CAPTAIN.MCP_SERVERS.FORM.AUTH_HEADER.NAME_LABEL')"
          :placeholder="
            t('CAPTAIN.MCP_SERVERS.FORM.AUTH_HEADER.NAME_PLACEHOLDER')
          "
          :message="
            v$.authHeaderName.$error
              ? t('CAPTAIN.MCP_SERVERS.FORM.AUTH_HEADER.NAME_ERROR')
              : ''
          "
          :message-type="v$.authHeaderName.$error ? 'error' : 'info'"
        />
        <Input
          v-model="state.authHeaderValue"
          type="password"
          :label="t('CAPTAIN.MCP_SERVERS.FORM.AUTH_HEADER.VALUE_LABEL')"
          :placeholder="
            hasSavedHeaderValue
              ? t(
                  'CAPTAIN.MCP_SERVERS.FORM.AUTH_HEADER.VALUE_PLACEHOLDER_SAVED'
                )
              : t('CAPTAIN.MCP_SERVERS.FORM.AUTH_HEADER.VALUE_PLACEHOLDER')
          "
          :message="
            v$.authHeaderValue.$error
              ? t('CAPTAIN.MCP_SERVERS.FORM.AUTH_HEADER.VALUE_ERROR')
              : ''
          "
          :message-type="v$.authHeaderValue.$error ? 'error' : 'info'"
        />
      </template>

      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input v-model="state.enabled" type="checkbox" />
        {{ t('CAPTAIN.MCP_SERVERS.FORM.ENABLED') }}
      </label>
    </div>
  </Dialog>
</template>
